import React from "react";
import { Link } from "react-router-dom";
import { formatDate } from "date";
import { button } from "components/designTokens";
import { Pen, Collection, Rubbish, Eye, Back, Next } from "components/icons";
import c from "classnames";

export function List({
  programs,
  categories,
  websiteUrl,
  page,
  isLastPage,
  onDeleteProgram,
  onSearchClicked,
  onChangePage,
}) {
  return (
    <div className="my-8">
      <Header onSearchClicked={onSearchClicked}></Header>
      <div key="filter" className="hidden px-4 mt-4">
        <label className="font-semibold mr-4">Category</label>
        <select className="p-1 border bg-white rounded">
          <option>ทั้งหมด</option>
          {categories.map((c) => (
            <option key={c.id}>{c.title}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col mt-8">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                      Program
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 bg-gray-50" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {programs.map((p) => (
                    <ProgramItem
                      key={p.id}
                      {...p}
                      websiteUrl={websiteUrl}
                      page={page}
                      onDeleteProgram={onDeleteProgram}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div
        key="list"
        className="bg-white shadow overflow-hidden sm:rounded-md mt-4"
      >
        <ul></ul>
      </div>
      <Paging
        page={page}
        isLastPage={isLastPage}
        onChangePage={onChangePage}
      ></Paging>
    </div>
  );
}

export function Header({ onSearchClicked }) {
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const { search_candidate } = e.target.elements;
    onSearchClicked(search_candidate.value);
  };

  const handleFocus = (e) => {
    e.target.select();
  };
  return (
    <div className="pb-5 border-gray-200 space-y-3 sm:flex sm:items-center sm:justify-between sm:space-x-4 sm:space-y-0">
      <h3 className="text-lg leading-6 font-medium text-gray-900">Program</h3>
      <div className="flex">
        <span className="shadow-sm rounded-md mr-4">
          <Link
            to="/create-program"
            className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
          >
            Create new program
          </Link>
        </span>
        <form onSubmit={handleSearchSubmit}>
          <label htmlFor="search_candidate" className="sr-only">
            Search
          </label>
          <div className="flex rounded-md shadow-sm">
            <div className="relative flex-grow focus-within:z-10">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {/* Heroicon name: search */}
                <svg
                  className="h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <input
                id="search_candidate"
                onFocus={handleFocus}
                name="search_candidate"
                className="hidden form-input w-full rounded-none rounded-l-md pl-10 transition ease-in-out duration-150 sm:block sm:text-sm sm:leading-5"
                placeholder="Search program"
              />
            </div>
            <button className="-ml-px relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium rounded-r-md text-gray-700 bg-gray-50 hover:text-gray-500 hover:bg-white focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150">
              <span className="ml-2">Search</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProgramItem({
  id,
  thumbnail,
  description,
  title,
  status,
  created_at,
  program_category,
  user,
  websiteUrl,
  page,
  onDeleteProgram,
}) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-no-wrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 w-24">
            <img className="w-full" src={thumbnail} alt={title} />
          </div>
          <div className="ml-4">
            <div className="text-sm leading-5 font-medium text-gray-900">
              {title}
            </div>
            <div className="text-sm leading-5 text-gray-500">
              <span className="inline-block mr-4">{`#${id}`}</span>
              <span className="inline-block">{user.display_name}</span>
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-2 whitespace-no-wrap">
        <div className="text-sm leading-5 text-gray-900">
          {formatDate(new Date(created_at))}
        </div>
      </td>
      <td className="px-6 py-2 whitespace-no-wrap">
        <div className="text-sm leading-5 text-gray-900">
          {program_category.title}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-no-wrap text-right text-sm leading-5 font-medium">
        <div className="flex justify-end">
          <a
            href={`${websiteUrl}/program/${program_category.slug}/${id}/`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
          >
            <Eye size={4}></Eye>
          </a>
          <Link
            to={`/program/${id}/episodes`}
            className="ml-1 inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
            title="Edit Episode"
          >
            <Collection size={4}></Collection>
          </Link>
          <Link
            to={`/program/${id}`}
            className="ml-1 inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
            title="Edit Program"
          >
            <Pen size={4}></Pen>
          </Link>
          <button
            onClick={(e) => {
              e.preventDefault();
              onDeleteProgram(id, page);
            }}
            className="ml-1 inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
          >
            <Rubbish size={4}></Rubbish>
          </button>
        </div>
      </td>
    </tr>
  );
}

export function Paging({ page, isLastPage, onChangePage }) {
  const prevCls = c(
    "border-gray-300 focus:outline-none relative inline-flex items-center px-2 py-2 rounded-l-md text-sm leading-5 font-medium border",
    {
      "bg-white text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150":
        page !== 1,
    },
    { "bg-gray-100 text-gray-300 cursor-not-allowed": page === 1 }
  );
  const nextCls = c(
    "border-gray-300 focus:outline-none relative inline-flex items-center px-2 py-2 rounded-l-md text-sm leading-5 font-medium border",
    {
      "bg-white text-gray-500 hover:text-gray-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-500 transition ease-in-out duration-150": !isLastPage,
    },
    { "bg-gray-100 text-gray-300 cursor-not-allowed": isLastPage }
  );
  return (
    <div className="flex items-center justify-center">
      <button
        onClick={(e) => {
          e.preventDefault();
          if (page > 1) {
            onChangePage(page - 1);
          }
        }}
        className={prevCls}
        aria-label="previous"
      >
        <Back></Back>
      </button>
      <button
        onClick={(e) => {
          e.preventDefault();
          if (!isLastPage) {
            onChangePage(page + 1);
          }
        }}
        className={nextCls}
        aria-label="next"
      >
        <Next></Next>
      </button>
    </div>
  );
}
